import React, { useState, useEffect } from "react";
import { Formik, Field, Form } from "formik";
import {
    InputGroup,
    FieldWrappers,
    Label,
    SubmitBtn,
    SideCancelBtn,
    CardWrapper,
    SelectionCard,
    Wrapper,
    ScrollableContent,
    ActionSection,
    RupeeIcon,
    ModalHeader
} from "../Styles/InputStyles";
import { toast } from "react-toastify";
import tw from "tailwind-styled-components";
import { MdClose, MdEdit } from "react-icons/md";
import * as Yup from "yup";
import { staff_roles } from "../../Config";
import SelectOptionModal from "../SelectOptionModal";
import { FormControl, MenuItem, Select } from "@mui/material";

const EditStaff = ({ editStaffLoading, editStaffMutate, setIsEditStaff, selectedData, setIsDeactivateStaff }) => {

    const [height, setHeight] = useState(window.innerHeight);
    const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            setHeight(window.innerHeight);
        };

        window.addEventListener('resize', handleResize);
        handleResize();

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const initialValues = {
        fullName: selectedData?.name || "",
        mobile: selectedData?.mobile || "",
        staff: selectedData?.role || "",
        gender: selectedData?.gender || "",
        salary: selectedData?.salary || "",
        commissionType: selectedData?.commissionType,
        commissionValue: selectedData?.commission || "",
    }

    const gender = [
        { value: 1, label: "Male" },
        { value: 2, label: "Female" },
    ];

    const staffRoleOptions = staff_roles.map(role => ({
        label: role.label,
        value: role.value,
    }));

    const handleSubmit = (values, { resetForm }) => {
        const nameRegex = /^[A-Za-z0-9. ]{4,}$/;
        const mobileRegex = /^\d{10}$/;
        const salaryRegex = /^\d{1,7}$/;
        const maxSalaryDeposit = 1000000;

        if (!nameRegex.test(values.fullName)) {
            toast.error("Only letters, numbers, spaces and dot are allowed.");
            return;
        }

        if (!mobileRegex.test(values.mobile)) {
            toast.error("Mobile number should be exactly 10 digits long and contain only numbers");
            return;
        }

        if (!values.gender) {
            toast.error("Gender is required");
            return;
        }
        if (!values.staff) {
            toast.error("Staff Role is required");
            return;
        }

        if (
            (values.salary &&
                !salaryRegex.test(values.salary)) ||
            parseInt(values.salary) > maxSalaryDeposit
        ) {
            toast.error(
                "Salary must be less than 10,00,000 and contain only numbers"
            );
            return;
        }

        if (values.commissionType !== null && values.commissionType !== undefined) {
            if (!values.commissionValue && values.commissionValue !== 0) {
                toast.error("Please enter commission value");
                return;
            }

            const commissionNum = Number(values.commissionValue);
            if (isNaN(commissionNum) || commissionNum < 0) {
                toast.error("Commission value must be a positive number");
                return;
            }

            if (values.commissionType === 1 && commissionNum > 100) {
                toast.error("Percentage cannot exceed 100%");
                return;
            }
        }
        const payload = {
            name: values.fullName,
            mobile: values.mobile,
            role: values.staff,
            gender: values.gender,
            salary: Number(values.salary),
            staffId: selectedData?.id,
        };

        if (values.commissionType !== null && values.commissionType !== undefined) {
            payload.commissionType = Number(values.commissionType);
            payload.commission = Number(values.commissionValue);
        }

        editStaffMutate(payload);
        resetForm();
    };

    const handleDeactivateStaff = () => {
        setIsEditStaff(false);
        setIsDeactivateStaff(true);
    };

    const validationSchema = Yup.object({
        fullName: Yup.string()
            .required('Name is required')
            .matches(/^[A-Za-z0-9.\s]+$/, 'Only letters, numbers, spaces and dot are allowed.')
            .min(4, 'Name must be at least 4 characters long'),
        staff: Yup.number()
            .required('Staff Role is required'),
        commissionType: Yup.mixed().nullable(),
        commissionValue: Yup.mixed().when('commissionType', {
            is: (val) => val !== null && val !== undefined,
            then: (schema) => schema.test(
                'is-valid-commission',
                'Commission value must be a positive number',
                (value) => {
                    if (!value && value !== 0) return false;
                    const numValue = Number(value);
                    return !isNaN(numValue) && numValue >= 0;
                }
            ),
            otherwise: (schema) => schema.notRequired(),
        }),
    });

    const handleIncrementTypeChange = (type, setFieldValue) => {
        // Convert string to number or keep as null
        const numericType = type === "" ? null : Number(type);
        setFieldValue("commissionType", numericType);
        setFieldValue("commissionValue", "");
    };

    const handleIncrementValueChange = (value, setFieldValue) => {
        setFieldValue("commissionValue", value);
    };

    return (
        <Container style={{ height: `${height}px`, overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <ModalHeader>
                <h1 className="text-white text-lg font-medium">Edit Staff</h1>
                <button onClick={() => setIsEditStaff(false)}>
                    <MdClose color="white" size={24} />
                </button>
            </ModalHeader>

            <div className="px-6 h-full flex flex-col justify-between">
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    enableReinitialize
                >
                    {({ values, setFieldValue, resetForm, errors, touched }) => {

                        // Get current staff role label
                        const getCurrentRoleLabel = () => {
                            const selectedRole = staff_roles.find(role => role.value === values?.staff);
                            return selectedRole ? selectedRole.label : "Select staff role";
                        };

                        const handleRoleSelect = (selectedValue) => {
                            setFieldValue("staff", selectedValue);
                        };

                        return (
                            <Form>
                                <ScrollableContent className="max-h-[calc(100vh-120px)]">
                                    <Wrapper>
                                        <InputGroup>
                                            <Label htmlFor="fullName">Name *</Label>
                                            <FieldWrappers>
                                                <Field
                                                    type="text"
                                                    name="fullName"
                                                    id="fullName"
                                                    autoComplete="off"
                                                />
                                            </FieldWrappers>
                                            {errors.fullName && touched.fullName && (
                                                <ErrorText>{errors.fullName}</ErrorText>
                                            )}
                                        </InputGroup>
                                        <InputGroup>
                                            <Label htmlFor="mobile">Mobile *</Label>
                                            <FieldWrappers className="bg-gray-100">
                                                <Field
                                                    type="text"
                                                    name="mobile"
                                                    id="mobile"
                                                    autoComplete="off"
                                                    maxLength={10}
                                                    required
                                                    readOnly
                                                />
                                            </FieldWrappers>
                                        </InputGroup>

                                        {/* Staff Role with SelectOptionModal */}
                                        <InputGroup>
                                            <Label htmlFor="staff">Staff Role *</Label>
                                            <FieldWrappers onClick={() => setIsRoleModalOpen(true)}>
                                                <Field
                                                    type="text"
                                                    name="staffDisplay"
                                                    id="staffDisplay"
                                                    autoComplete="off"
                                                    readOnly
                                                    value={getCurrentRoleLabel()}
                                                    className="w-full border-none outline-none bg-transparent cursor-pointer"
                                                />
                                                <MdEdit className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors duration-200 flex-shrink-0 mr-2" />
                                            </FieldWrappers>
                                            {errors.staff && touched.staff && (
                                                <ErrorText>{errors.staff}</ErrorText>
                                            )}
                                        </InputGroup>

                                        <InputGroup>
                                            <Label>Gender *</Label>
                                            <CardWrapper>
                                                {gender.map((mode) => (
                                                    <SelectionCard
                                                        key={mode.value}
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            setFieldValue("gender", mode.value, false);
                                                        }}
                                                        className={
                                                            values.gender === mode.value
                                                                ? "bg-[#0078BD] text-white"
                                                                : "bg-gray-100 text-gray-800"
                                                        }
                                                    >
                                                        <span className="text-sm">{mode.label}</span>
                                                    </SelectionCard>
                                                ))}
                                            </CardWrapper>
                                        </InputGroup>

                                        <InputGroup>
                                            <Label htmlFor="salary">Monthly Salary </Label>
                                            <FieldWrappers>
                                                <RupeeIcon />
                                                <Field
                                                    type="number"
                                                    name="salary"
                                                    id="salary"
                                                    autoComplete="off"
                                                    className="w-full pl-5"
                                                />
                                            </FieldWrappers>
                                        </InputGroup>

                                        {/* Commission Type - Optional */}
                                        <InputGroup>
                                            <Label htmlFor="commissionType">
                                                Commission Type
                                            </Label>
                                            <FormControl fullWidth>
                                                <Select
                                                    id="commissionType"
                                                    name="commissionType"
                                                    value={values.commissionType ?? ""}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        handleIncrementTypeChange(value, setFieldValue);
                                                    }}
                                                    displayEmpty
                                                    style={{ height: "46px" }}
                                                    className="px-2"
                                                    renderValue={(selected) => {
                                                        if (selected === null || selected === undefined || selected === "") {
                                                            return (
                                                                <span className="text-gray-400">
                                                                    Select commission type (Optional)
                                                                </span>
                                                            );
                                                        }
                                                        return selected === 0
                                                            ? "Flat Amount (₹)"
                                                            : "Percentage (%)";
                                                    }}
                                                >
                                                    <MenuItem value="">
                                                        <em className="text-gray-400">None</em>
                                                    </MenuItem>
                                                    <MenuItem value={0}>Flat Amount (₹)</MenuItem>
                                                    <MenuItem value={1}>Percentage (%)</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </InputGroup>

                                        {/* Commission Value - Only show if commission type is selected */}
                                        {values.commissionType !== null &&
                                            values.commissionType !== undefined &&
                                            values.commissionType !== "" && (
                                                <InputGroup>
                                                    <Label htmlFor="commissionValue">
                                                        {values.commissionType === 0
                                                            ? "Flat Amount (₹)"
                                                            : "Percentage (%)"}
                                                    </Label>
                                                    <FieldWrappers>
                                                        {values.commissionType === 0 && <RupeeIcon />}
                                                        <Field
                                                            type="number"
                                                            name="commissionValue"
                                                            id="commissionValue"
                                                            placeholder={
                                                                values.commissionType === 0
                                                                    ? "Enter amount"
                                                                    : "Enter %"
                                                            }
                                                            value={values.commissionValue}
                                                            onChange={(e) =>
                                                                handleIncrementValueChange(
                                                                    e.target.value,
                                                                    setFieldValue,
                                                                )
                                                            }
                                                            className={
                                                                values.commissionType === 0 ? "pl-6" : ""
                                                            }
                                                            min="0"
                                                            max={
                                                                values.commissionType === 1
                                                                    ? "100"
                                                                    : undefined
                                                            }
                                                            step="any"
                                                        />
                                                        {values.commissionType === 1 && (
                                                            <span className="absolute right-3 text-gray-500">
                                                                %
                                                            </span>
                                                        )}
                                                    </FieldWrappers>
                                                    {errors.commissionValue && touched.commissionValue && (
                                                        <ErrorText>{errors.commissionValue}</ErrorText>
                                                    )}
                                                </InputGroup>
                                            )}
                                    </Wrapper>
                                </ScrollableContent>
                                <ActionSection>
                                    <SideCancelBtn type="button" onClick={handleDeactivateStaff}>
                                        {selectedData?.status === 1 ? "Deactivate Staff" : "Activate Staff"}
                                    </SideCancelBtn>
                                    <SubmitBtn type="submit" disabled={editStaffLoading}>
                                        {editStaffLoading ? "Processing..." : "Edit Staff"}
                                    </SubmitBtn>
                                </ActionSection>

                                {/* Staff Role Selection Modal */}
                                <SelectOptionModal
                                    isOpen={isRoleModalOpen}
                                    onClose={() => setIsRoleModalOpen(false)}
                                    title="Select Staff Role"
                                    options={staffRoleOptions}
                                    selectedValue={values.staff}
                                    onSelect={handleRoleSelect}
                                    searchPlaceholder="Search staff roles..."
                                />
                            </Form>
                        );
                    }}
                </Formik>
            </div>
        </Container>
    );
};

const Container = tw.div`bg-white w-full h-full flex flex-col relative`;
const Header = tw.div`flex items-center justify-between bg-primary-color py-4 px-4`;
const ErrorText = tw.div`text-red-500 text-sm ml-0.5`;

export default EditStaff;